import { Injectable } from '@angular/core';
import { LocalStorageService } from 'ngx-webstorage';
import jwt_decode from 'jwt-decode';
import {
  CompanyInfos,
  RoleTypeEnum,
  User,
  UserGroup,
} from '@pulpo/pulpo-models';

@Injectable({ providedIn: 'root' })
export class StorageService {
  private readonly REFRESH_LOCK_KEY = 'pulpo_refresh_lock';
  private readonly REFRESH_LOCK_TTL_MS = 10_000;

  constructor(private localStorage: LocalStorageService) {}

  clear() {
    this.localStorage.clear();
  }

  public getProducerGroup(): UserGroup {
    return this.localStorage.retrieve('producerGroup');
  }

  public getCompanyInfos(): CompanyInfos | undefined {
    const producerGroup = this.getProducerGroup();
    return producerGroup ? producerGroup.companyInfos : undefined;
  }

  public setProducerGroup(producerGroup: UserGroup) {
    this.localStorage.store('producerGroup', producerGroup);
  }

  public setToken(token: string) {
    this.localStorage.store('authenticationToken', token);
  }

  public getToken() {
    return this.localStorage.retrieve('authenticationToken');
  }

  public getRefreshToken() {
    return this.localStorage.retrieve('refreshToken');
  }

  public setRefreshToken(refreshToken: string) {
    this.localStorage.store('refreshToken', refreshToken);
  }

  public setUser(user: User) {
    this.localStorage.store('user', user);
  }

  public getUser(): User {
    return this.localStorage.retrieve('user');
  }

  public getTokenExpirationTime() {
    const token = this.getToken();
    if (token) {
      const decodedToken: any = jwt_decode(token);
      return decodedToken?.exp;
    } else {
      return null;
    }
  }

  public getRefreshTokenExpirationTime() {
    const refreshToken = this.getRefreshToken();
    if (refreshToken) {
      const decodedToken: any = jwt_decode(refreshToken);
      return decodedToken?.exp;
    } else {
      return null;
    }
  }

  // The role is carried in the JWT 'role' claim as a JSON-serialized RoleLightDTO.
  public getSessionRoleType(): RoleTypeEnum | undefined {
    const token = this.getToken();
    if (!token) {
      return undefined;
    }
    try {
      const decodedToken: any = jwt_decode(token);
      const role =
        typeof decodedToken?.role === 'string'
          ? JSON.parse(decodedToken.role)
          : decodedToken?.role;
      return role?.roleType as RoleTypeEnum;
    } catch {
      return undefined;
    }
  }

  public setFlightSearch(search: any) {
    this.localStorage.store('flightSearch', search);
  }

  public getFlightSearch(): any {
    return this.localStorage.retrieve('flightSearch');
  }

  acquireRefreshLock(): boolean {
    const existing = window.localStorage.getItem(this.REFRESH_LOCK_KEY);
    if (existing && Date.now() - parseInt(existing, 10) < this.REFRESH_LOCK_TTL_MS) return false;
    window.localStorage.setItem(this.REFRESH_LOCK_KEY, String(Date.now()));
    return true;
  }

  releaseRefreshLock(): void {
    window.localStorage.removeItem(this.REFRESH_LOCK_KEY);
  }

  isRefreshLocked(): boolean {
    const existing = window.localStorage.getItem(this.REFRESH_LOCK_KEY);
    return !!existing && Date.now() - parseInt(existing, 10) < this.REFRESH_LOCK_TTL_MS;
  }
}
